import React, { useState } from 'react';
import { Eye, EyeOff } from 'lucide-react';
import Header from '../components/Header';
import { useTranslations } from '../translations';
import { resetPassword } from '../services/auth';
import { useNotification } from '../components/NotificationProvider';
import { isPasswordValid, PASSWORD_MAX_LENGTH } from '../utils/passwordValidation';
import './ResetPassword.css';

interface ResetPasswordProps {
  token: string;
  onNavigateToLogin: () => void;
  onNavigateToHome: () => void;
}

const ResetPassword: React.FC<ResetPasswordProps> = ({ token, onNavigateToLogin, onNavigateToHome }) => {
  const t = useTranslations();
  const { showNotification } = useNotification();
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<Set<string>>(new Set());
  const [formData, setFormData] = useState({
    password: '',
    confirmPassword: ''
  });

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    setError(null);
    if (value.trim()) {
      setValidationErrors(prev => {
        const newErrors = new Set(prev);
        newErrors.delete(name);
        return newErrors;
      });
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    const errors = new Set<string>();
    if (!isPasswordValid(formData.password)) {
      errors.add('password');
      setError(t.resetPassword.passwordValidation);
    }
    if (!formData.confirmPassword.trim()) errors.add('confirmPassword');

    if (formData.password.trim() && formData.confirmPassword.trim() && formData.password !== formData.confirmPassword) {
      setError(t.resetPassword.passwordMismatch);
      errors.add('password');
      errors.add('confirmPassword');
    }

    if (errors.size > 0) {
      setValidationErrors(errors);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      await resetPassword({
        resetKey: token,
        newPassword: formData.password,
        confirmedNewPassword: formData.confirmPassword
      });

      showNotification(t.resetPassword.resetSuccess, 'success');
      onNavigateToLogin();
    } catch (err) {
      showNotification(t.resetPassword.resetError, 'error');
      setError(t.resetPassword.resetError);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="reset-password-page">
      <Header
        isLoggedIn={false}
        onNavigateToLogin={onNavigateToLogin}
        onNavigateToRegister={() => {}}
        onNavigateToHome={onNavigateToHome}
      />
      <div className="reset-password-content">
        <div className="reset-password-form-container">
          <div className="reset-password-form-wrapper">
            <h1 className="reset-password-title">{t.resetPassword.title}</h1>
            <p className="reset-password-description">{t.resetPassword.description}</p>
            
            <form className="reset-password-form" onSubmit={handleSubmit}>
              <div className="form-field form-field-full">
                <label className="form-label">
                  {t.resetPassword.newPassword}
                  <span className="required-asterisk">*</span>
                </label>
                <div className="password-input-wrapper">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    name="password"
                    className={`form-input password-input ${validationErrors.has('password') ? 'form-input-error' : ''}`}
                    placeholder={t.resetPassword.passwordPlaceholder}
                    value={formData.password}
                    onChange={handleInputChange}
                    maxLength={PASSWORD_MAX_LENGTH}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowPassword(!showPassword)}
                    aria-label={showPassword ? t.resetPassword.hidePassword : t.resetPassword.showPassword}
                  >
                    {showPassword
                      ? <EyeOff className="password-toggle-icon" aria-hidden="true" />
                      : <Eye className="password-toggle-icon" aria-hidden="true" />}
                  </button>
                </div>
              </div>

              <div className="form-field form-field-full">
                <label className="form-label">
                  {t.resetPassword.confirmPassword}
                  <span className="required-asterisk">*</span>
                </label>
                <div className="password-input-wrapper">
                  <input
                    type={showConfirmPassword ? 'text' : 'password'}
                    name="confirmPassword"
                    className={`form-input password-input ${validationErrors.has('confirmPassword') ? 'form-input-error' : ''}`}
                    placeholder={t.resetPassword.confirmPasswordPlaceholder}
                    value={formData.confirmPassword}
                    onChange={handleInputChange}
                    maxLength={PASSWORD_MAX_LENGTH}
                  />
                  <button
                    type="button"
                    className="password-toggle"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    aria-label={showConfirmPassword ? t.resetPassword.hidePassword : t.resetPassword.showPassword}
                  >
                    {showConfirmPassword
                      ? <EyeOff className="password-toggle-icon" aria-hidden="true" />
                      : <Eye className="password-toggle-icon" aria-hidden="true" />}
                  </button>
                </div>
              </div>

              {error && (
                <div className="error-message">
                  {error}
                </div>
              )}

              <button type="submit" className="btn-submit" disabled={isLoading}>
                {isLoading ? 'Se încarcă...' : t.resetPassword.submitButton}
              </button>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;
